import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Grid,
  CircularProgress,
} from '@mui/material'
import { Print, ArrowBack } from '@mui/icons-material'
import { Button } from '@components/common'
import { toast } from 'react-toastify'
import api from '@services/api'

const JobCardPrint = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const [jobCard, setJobCard] = useState(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchJobCard = async () => {
      try {
        const response = await api.get(`/maintenance/job-cards/${id}/print_preview/`)
        setJobCard(response.data)
      } catch (error) {
        console.error('Error fetching job card:', error)
        toast.error('Failed to load job card')
        navigate('/maintenance/job-cards')
      } finally {
        setLoading(false)
      }
    }
    fetchJobCard()
  }, [id, navigate])

  const handlePrint = () => {
    window.print()
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    )
  }

  if (!jobCard) {
    return null
  }

  return (
    <>
      {/* Print Controls - Hidden when printing */}
      <Box sx={{ p: 2, display: 'flex', gap: 2, '@media print': { display: 'none' } }}>
        <Button
          startIcon={<ArrowBack />}
          variant="outlined"
          onClick={() => navigate('/maintenance/job-cards')}
        >
          Back
        </Button>
        <Button
          startIcon={<Print />}
          onClick={handlePrint}
        >
          Print
        </Button>
      </Box>

      {/* Printable Content */}
      <Box
        sx={{
          maxWidth: '210mm',
          margin: '0 auto',
          p: 4,
          bgcolor: 'white',
          '@media print': {
            m: 0,
            p: 2,
            maxWidth: 'none',
          },
        }}
      >
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Typography variant="h5" fontWeight="bold">
            UGANDA CANCER INSTITUTE
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Computerized Maintenance Management System
          </Typography>
          <Typography variant="h6" sx={{ mt: 2, fontWeight: 'bold', textDecoration: 'underline' }}>
            MAINTENANCE JOB CARD
          </Typography>
        </Box>

        {/* Job Card Number */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="body1">
            <strong>Job Card #:</strong> {jobCard.job_card_number}
          </Typography>
          <Typography variant="body1">
            <strong>Work Order #:</strong> {jobCard.work_order_number}
          </Typography>
          <Typography variant="body1">
            <strong>Date:</strong> {jobCard.generated_date ? new Date(jobCard.generated_date).toLocaleDateString() : '-'}
          </Typography>
        </Box>

        <Divider sx={{ mb: 2 }} />

        {/* Asset Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          EQUIPMENT INFORMATION
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Asset Name</TableCell>
                <TableCell sx={{ width: '25%' }}>{jobCard.asset_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Asset Tag</TableCell>
                <TableCell sx={{ width: '25%' }}>{jobCard.asset_tag || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Model Number</TableCell>
                <TableCell>{jobCard.asset_model_number || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Serial Number</TableCell>
                <TableCell>{jobCard.asset_serial_number || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Location</TableCell>
                <TableCell>{jobCard.asset_location || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Department</TableCell>
                <TableCell>{jobCard.department_name || '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Work Order Information */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          WORK ORDER DETAILS
        </Typography>
        <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
          <Table size="small">
            <TableBody>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', width: '25%' }}>Title</TableCell>
                <TableCell colSpan={3}>{jobCard.work_order_title || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Category</TableCell>
                <TableCell>{jobCard.work_order_category || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Priority</TableCell>
                <TableCell>{jobCard.work_order_priority || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Assigned To</TableCell>
                <TableCell>{jobCard.assigned_to_name || '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Hours Worked</TableCell>
                <TableCell>{jobCard.actual_hours || '-'}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>Start Time</TableCell>
                <TableCell>{jobCard.start_time ? new Date(jobCard.start_time).toLocaleString() : '-'}</TableCell>
                <TableCell sx={{ fontWeight: 'bold' }}>Completion Time</TableCell>
                <TableCell>{jobCard.completion_time ? new Date(jobCard.completion_time).toLocaleString() : '-'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        {/* Problem Description */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          PROBLEM DESCRIPTION
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, minHeight: 60 }}>
          <Typography variant="body2">
            {jobCard.problem_description || jobCard.work_order_description || 'No description provided'}
          </Typography>
        </Paper>

        {/* Resolution */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
          WORK PERFORMED / RESOLUTION
        </Typography>
        <Paper variant="outlined" sx={{ p: 2, mb: 3, minHeight: 60 }}>
          <Typography variant="body2">
            {jobCard.resolution_description || 'No resolution description provided'}
          </Typography>
        </Paper>

        {/* Parts Used */}
        {jobCard.parts_used && jobCard.parts_used.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              PARTS / MATERIALS USED
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Part Name</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Part #</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Qty</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Unit Cost</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobCard.parts_used.map((part, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{part.part_name || part.spare_part_name}</TableCell>
                      <TableCell>{part.part_number || '-'}</TableCell>
                      <TableCell align="center">{part.quantity}</TableCell>
                      <TableCell align="right">{part.unit_cost?.toLocaleString() || '-'}</TableCell>
                      <TableCell align="right">{part.total_cost?.toLocaleString() || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Labor Records */}
        {jobCard.labor_records && jobCard.labor_records.length > 0 && (
          <>
            <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
              LABOR RECORDS
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell sx={{ fontWeight: 'bold' }}>Technician</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }}>Task Description</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="center">Hours</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Rate</TableCell>
                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {jobCard.labor_records.map((labor, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{labor.technician_name}</TableCell>
                      <TableCell>{labor.task_description || '-'}</TableCell>
                      <TableCell align="center">{labor.hours_worked}</TableCell>
                      <TableCell align="right">{labor.hourly_rate?.toLocaleString() || '-'}</TableCell>
                      <TableCell align="right">{labor.total_cost?.toLocaleString() || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {/* Signatures Section */}
        <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 2 }}>
          SIGNATURES
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={6}>
            <Box sx={{ borderBottom: '1px solid black', mb: 1, height: 40 }} />
            <Typography variant="body2"><strong>Technician Signature</strong></Typography>
            <Typography variant="body2">{jobCard.assigned_to_name || '_______________'}</Typography>
            <Typography variant="body2">Date: _______________</Typography>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ borderBottom: '1px solid black', mb: 1, height: 40 }} />
            <Typography variant="body2"><strong>Department Head / Supervisor</strong></Typography>
            <Typography variant="body2">{jobCard.acknowledged_by_name || '_______________'}</Typography>
            <Typography variant="body2">Date: {jobCard.acknowledged_at ? new Date(jobCard.acknowledged_at).toLocaleDateString() : '_______________'}</Typography>
          </Grid>
        </Grid>

        {/* Footer */}
        <Box sx={{ mt: 4, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="caption" color="text.secondary">
            Generated by UCI CMMS on {jobCard.print_date ? new Date(jobCard.print_date).toLocaleString() : new Date().toLocaleString()}
            {jobCard.printed_by && ` | Printed by: ${jobCard.printed_by}`}
          </Typography>
        </Box>
      </Box>

      {/* Print Styles */}
      <style>
        {`
          @media print {
            body * {
              visibility: hidden;
            }
            .MuiBox-root, .MuiBox-root * {
              visibility: visible;
            }
            @page {
              size: A4;
              margin: 10mm;
            }
          }
        `}
      </style>
    </>
  )
}

export default JobCardPrint
